import { createSelector } from "reselect";

import { selectors as dateFilterSelectors } from "@stubhub/react-date-filter";
import { Controller } from "@stubhub/react-store-provider";

import { storeToSearchRadiusMapping } from "./event-list-panel-helper";
import { searchEvents } from "./utils/api";
import DateTimeFormat from "./utils/datetimeformat";

const ACTION_TYPES = {
  EVENTLISTPANEL_LOADEVENTS: "EventListPanel.loadEvents",
};

function generateActionCreators(actionType) {
  return {
    loadEvents: (props, reset) => {
      /* Ignore default value assignments */
      /* istanbul ignore next */
      let {
        geo,
        events = [],
        dateFilter,
        includeRadius,
        sort,
        reRankBy,
        offset = 0,
        shstoreId,
        spellCheck,
        id,
        disableGeoLocation,
        disableUrgency,
      } = props;
      const searchParams = { ...props.searchParams };

      /**
       * Geo Location Section
       *
       */
      if (!disableGeoLocation && geo && geo.key) {
        searchParams.point =
          geo.latitude && geo.longitude
            ? `${geo.latitude},${geo.longitude}`
            : null;
        searchParams.excludeFromRadius = !includeRadius;
      }

      /**
       * Use the Radius id passed externally
       *
       */
      searchParams.radius =
        searchParams.radius || storeToSearchRadiusMapping(shstoreId);

      if (reset) {
        events = [];
        offset = 0;
      }

      searchParams.dateLocal =
        dateFilter && dateFilter.start && dateFilter.end
          ? `${DateTimeFormat.toAPIString(
              dateFilter.start
            )} TO ${DateTimeFormat.toAPIString(dateFilter.end)}`
          : null;
      searchParams.sort = sort;
      searchParams.reRankBy = reRankBy;
      searchParams.start = offset;
      searchParams.shstore = shstoreId;
      searchParams.spellCheck = spellCheck;
      searchParams.disableUrgency = disableUrgency;

      return (dispatch, getState, { logger }) => {
        searchParams.logger = logger;

        dispatch({ type: actionType, loading: true, events });

        return searchEvents(searchParams, getState().lang)
          .then((body) => {
            events = events.concat(body.events);
            offset += body.events.length;
            const { numFound, didYouMean } = body;
            const hasMore = offset < numFound;
            dispatch({
              type: actionType,
              loading: false,
              events,
              numFound,
              offset,
              hasMore,
              didYouMean,
              localStateId: id,
            });

            return "";
          })
          .catch({});
      };
    },

    /**
     * Method to Set the Sort State of the component
     *
     */
    setSort: (sort) => {
      return (dispatch) => {
        dispatch({ type: actionType, sort });
      };
    },

    /**
     * Method to reset the Events
     *
     */
    resetEvents: () => (dispatch) => dispatch({ type: `${actionType}:RESET` }),
  };
}

const makeLocalSelector = () =>
  createSelector(
    (state) => state.locale,
    (locale) => locale || {}
  );
const makeEventsSelector = () =>
  createSelector(
    (state) => state.events,
    (events) => events || []
  );
const makeGeoSelector = () =>
  createSelector(
    (state) => state.user,
    (userState) => (userState ? userState.location : null)
  );
const makeLocalStateIdSelector = () =>
  createSelector(
    (localState) => localState.localStateId,
    (localStateId) => localStateId
  );
const makeFilterSelector = () => dateFilterSelectors.dateFilter;
const makeSortSelector = () =>
  createSelector(
    (localState) => localState.sort || "",
    (sort) => sort
  );

export default function produceController(namespace) {
  const localSelector = makeLocalSelector();
  const eventsSelector = makeEventsSelector();
  const geoSelector = makeGeoSelector();
  const filterSelector = makeFilterSelector();
  const sortSelector = makeSortSelector();
  const localStateIdSelector = makeLocalStateIdSelector();

  const actionType = `${namespace}:${ACTION_TYPES.EVENTLISTPANEL_LOADEVENTS}`;
  const controller = new Controller({
    namespace,
    mapStateToProps(state) {
      const localState = this.getLocalState(state);

      return {
        didYouMean: localState.didYouMean,
        shstoreId: (state.gsConfig || {}).shstoreId,
        local: localSelector(localState),
        events: eventsSelector(localState),
        numFound: localState.numFound || 0,
        loading: localState.loading !== false,
        offset: localState.offset || 0,
        hasMore: localState.hasMore || false,
        localStateId: localStateIdSelector(localState),
        geo: geoSelector(state),
        dateFilter: filterSelector(state),
        reset: localState.__reset__ === true,
        sort: sortSelector(localState),
      };
    },
    actionCreators: generateActionCreators(actionType),
    reducers: [actionType],
  });
  // Add below line just to monitor filter changes
  controller.addResetReducer([
    "SEARCH_RESET",
    "USER_LOCATION_CHANGE",
    "USER_DATE_FILTER_CHANGED",
    `${actionType}:RESET`,
    "SEARCH_SPELLING_CHECK_UPDATED",
    "ENTITY_RESET",
    "ENTITY_GROUPING_FILTER_UPDATED",
  ]);

  return controller;
}
